import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSortModule, Sort } from '@angular/material/sort';
import { EstablishmentService } from '../../services/establishment/establishment.service';
import { ToastService } from '../../services/toast/toast.service';
import { PermissionService } from '../../services/permission/permission.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { AddStaffDialogComponent } from './add-staff-dialog/add-staff-dialog.component';
import { ManageUserDialogComponent } from './manage-user-dialog/manage-user-dialog.component';
import { establishmentUserAccessApi } from './establishment-user-access-api';
import { sortTableRows } from '../../common-files/table-sort';

@Component({
  selector: 'app-establishment-staff',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatSortModule, LoaderComponent],
  templateUrl: './establishment-staff.component.html',
  styleUrl: './establishment-staff.component.scss',
})
export class EstablishmentStaffComponent implements OnInit {
  staff: any[] = [];
  isLoading: boolean = false;
  canManage: boolean = false;
  search = new FormControl('');
  private sortState: Sort = { active: '', direction: '' };

  constructor(
    private establishmentService: EstablishmentService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.canManage = this.permissionService.can('branch-staff', 'can_manage_users');
    this.load();
  }

  private activeEstId(): number | null {
    const value = localStorage.getItem('activeEstablishmentId');
    return value ? Number(value) : null;
  }

  load(): void {
    this.isLoading = true;
    this.establishmentService.listStaff(this.activeEstId()).subscribe({
      next: (res: any) => {
        this.staff = this.applySort(res?.data?.staff || []);
        this.isLoading = false;
      },
      error: (err) => {
        this.staff = [];
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load staff.');
      },
    });
  }

  // top-right search: name, username or mobile
  filteredStaff(): any[] {
    const term = (this.search.value || '').trim().toLowerCase();
    if (!term) return this.staff;
    return this.staff.filter((member) =>
      `${member.fullName} ${member.userName} ${member.mobileNumber || ''}`.toLowerCase().includes(term)
    );
  }

  sortStaff(sort: Sort): void {
    this.sortState = sort;
    this.staff = this.applySort(this.staff);
  }

  private applySort(staff: any[]): any[] {
    return sortTableRows(staff, this.sortState, {
      access: (member) => (member.isRestricted ? 'Restricted' : 'Active'),
    });
  }

  addStaff(): void {
    if (!this.canManage) return;

    const ref = this.dialog.open(AddStaffDialogComponent, {
      width: '680px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100dvh - 32px)',
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  // roles + permissions of one staff member (view-only without can_manage_users)
  manage(member: any): void {
    const ref = this.dialog.open(ManageUserDialogComponent, {
      width: '900px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100dvh - 32px)',
      data: {
        sumId: member.sumId,
        name: member.fullName,
        api: establishmentUserAccessApi(this.establishmentService, this.permissionService),
      },
    });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) this.load();
    });
  }

  toggleRestricted(member: any): void {
    if (!this.canManage || member.isSelf) return;

    const restrict = !member.isRestricted;
    this.isLoading = true;
    this.establishmentService.setStaffRestricted(member.sumId, restrict).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Updated.');
        this.load();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to update staff access.');
      },
    });
  }
}
